import { useState } from 'react'
import { KeyRound, ShieldCheck, AlertCircle } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { userAdminAPI } from '@/lib/api'

/**
 * ForcePasswordChange — blokkeert de app tot een geïmporteerde gebruiker
 * zijn tijdelijke wachtwoord vervangt. Geen wegklik-optie: het tijdelijke
 * wachtwoord is bij de import op papier gezet, dus zolang het geldig is
 * kan iemand anders ermee inloggen.
 */
export default function ForcePasswordChange({ onDone }) {
  const [current, setCurrent] = useState('')
  const [next, setNext]       = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy]       = useState(false)
  const [error, setError]     = useState(null)

  const submit = async (e) => {
    e.preventDefault()
    setError(null)

    if (next.length < 6)  return setError('Kies een wachtwoord van minstens 6 tekens.')
    if (next !== confirm) return setError('De twee nieuwe wachtwoorden zijn niet gelijk.')
    if (next === current) return setError('Kies iets anders dan je tijdelijke wachtwoord.')

    setBusy(true)
    try {
      await userAdminAPI.changePassword(current, next)
      onDone?.()
    } catch (e) {
      setError(e.response?.data?.error ?? e.message)
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 bg-background flex items-center justify-center p-6">
      <Card className="w-full max-w-md" style={{ borderLeft: '2px solid rgb(var(--acid-rgb))' }}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0">
              <KeyRound className="h-5 w-5 text-acid" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Eerste keer inloggen
              </div>
              <h1 className="text-lg font-bold leading-tight">Kies je eigen wachtwoord</h1>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
            Je account is aangemaakt met een tijdelijk wachtwoord. Kies nu iets van jezelf —
            daarna kun je de rest van de site gebruiken.
          </p>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Tijdelijk wachtwoord</Label>
              <Input type="password" value={current} autoFocus required
                     onChange={e => setCurrent(e.target.value)}
                     placeholder="Het wachtwoord dat je gekregen hebt" />
            </div>
            <div>
              <Label>Nieuw wachtwoord</Label>
              <Input type="password" value={next} required
                     onChange={e => setNext(e.target.value)}
                     placeholder="Minstens 6 tekens" />
            </div>
            <div>
              <Label>Nogmaals ter controle</Label>
              <Input type="password" value={confirm} required
                     onChange={e => setConfirm(e.target.value)} />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-signal">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={busy}>
              <ShieldCheck className="h-4 w-4 mr-1.5" />
              {busy ? 'Bezig…' : 'Wachtwoord instellen'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
